import { Injectable } from '@angular/core';

import { AccountProfileService } from '../lib/accountProfile.service';
import { DateService } from './date.service';
import { DealershipService } from '../lib/dealership.service';
import { KeyValuePairService } from '../lib/keyValuePair.service';
import { SessionService } from '../lib/session.service';
import { SettingsService } from '../lib/settings.service';
import { UtilsService } from '../lib/utils.service';

import type {
    TAccountProfile, TCoordinate, TCustomer, TDealership,
    TRootSessionData, TSetting, TTimestamp
} from '@shared/models';
import { Constants } from '@shared/models';
const DateTimeFormats = Constants.DateTimeFormats;

@Injectable({
    'providedIn': 'root'
})
export class VariableReplacementService {
    private settings!: TSetting;
    private accountProfile!: TAccountProfile;
    private dealership!: TDealership;

    constructor(private sessionService: SessionService, private settingsService: SettingsService,
        private utilsService: UtilsService, private accountProfileService: AccountProfileService,
        private keyValuePairService: KeyValuePairService, private dateService: DateService,
        private dealershipService: DealershipService) {
    }

    // accountNumber getter
    public get accountNumber(): string {
        return this.accountProfile.accountNumber;
    }

    // campaignLink
    public get campaignLink(): string {
        let link = '';

        if (this.hostingEnabled) {
            const queryParams = `an=${this.accountNumber}&cu=${this.customerUid}`;
            // const link = this.settings?.hosting.campaignHostingLink;
            link = this.hostingLink + this.queryParams;
        } else {
            link = this.hostingLink;
        }

        return link;
    }

    // countryCode
    public get countryCode(): string {
        return this.dealership.address.isoCountryCode;
    }

    // incentiveSetId
    public get incentiveSetId(): number {
        return this.keyValuePairService.getValue('[IncentiveSetId]') as number;
    }
    public set incentiveSetId(value: number) {
        this.keyValuePairService.setValue('[IncentiveSetId]', value);
    }

    // currentCoordinate
    public get currentCoordinate(): TCoordinate {
        return this.keyValuePairService.getValue('[CurrentCoordinate]') as TCoordinate;
    }
    public set currentCoordinate(value: TCoordinate) {
        this.keyValuePairService.setValue('[CurrentCoordinate]', value);
    }

    // currentDate
    public get currentDate(): string {
        const str = this.dateService.formatTimestamp('now', 'PPP');
        return str;
    }

    // CurrentTime
    public get currentTime(): string {
        const str = this.dateService.formatTimestamp('now', 'h:mm a');
        return str;
    }

    // customer
    public get customer(): TCustomer {
        return this.keyValuePairService.getValue('[Customer]') as TCustomer;
    }
    public set customer(value: TCustomer) {
        this.keyValuePairService.setValue('[Customer]', value);
    }

    public get customerCellPhoneWithCountryCode(): string {
        const customer = this.customer;
        const cellPhone = customer?.cellPhone;
        const cellPhoneWithCountryCode = this.countryCode + cellPhone;
        return cellPhoneWithCountryCode;
    }

    public get cellPhone(): string {
        const customer = this.customer;
        return customer?.cellPhone ?? '';
    }
    
    // customerName
    public get customerName(): string {
        const customer: TCustomer = this.keyValuePairService.getValue('[Customer]') as TCustomer;
        const customerName = customer?.customerName;
        return customerName;
    }
    public set customerName(value: string) {
        this.keyValuePairService.setValue('[CustomerName]', value);
    }

    // customerUid
    public get customerUid(): string {
        const customer = this.customer;
        return customer.uid;
    }

    // dealershipName
    public get dealershipName(): string {
        return this.dealership.dealershipName;
    }

    // debugData
    public get debugData(): string {
        return this.keyValuePairService.getValue('[DebugData]') as string;
    }
    public set debugData(value: string) {
        this.keyValuePairService.setValue('[DebugData]', value);
    }

    // departureTime
    public get departureTime(): string {
        const departureTime = this.numMinutesFromNow;
        return departureTime;
        // return this.keyValuePairService.getValue( '[DepartureTime]' );
    }

    // distance
    public get distance(): number {
        return this.keyValuePairService.getValue('[Distance]') as number;
    }
    public set distance(value: number) {
        this.keyValuePairService.setValue('[Distsance]', value);
    }

    // distanceFormatted
    public get distanceFormatted(): string {
        // const value = this.keyValuePairService.getValue( '[Distance]' );
        return this.keyValuePairService.getValue('[Distance]') as string;
        // return value?.toString();
        // let distance: TDistance = null;
        // if (this.variables.currentCoordinate !== null && this.variables.toCoordinate !== null) {
        //     distance = this.utilsService.calculateDistance(this.variables.currentCoordinate, this.variables.toCoordinate);
        // }
    }

    // domain
    public get domain(): string {
        return this.utilsService.getDomainInfo().domain;
    }

    // fakeCustomer
    public get fakeCustomer(): TCustomer {
        return this.sessionService.getFakeCustomer();
    }

    public get fakeDepartureTime(): string {
        return this.keyValuePairService.getValue('[FakeDepartureTime]') as string;
    }
    public set fakeDepartureTime(value: string) {
        this.keyValuePairService.setValue('[FakeDepartureTime]', value);
    }

    // hostingEnabled
    public get hostingEnabled(): boolean {
        const enabled = this.settings.hosting.enabled;
        return enabled;
    }

    // hostingLink
    public get hostingLink(): string {
        let link = '';

        if (this.hostingEnabled) {
            const domainInfo = this.utilsService.getDomainInfo();
            const path = 'showCampaign?';
            // link = 'https://app.ShuttleNomix.com/showCampaign?';
            link = domainInfo.properOrigin + '/' + path;
        } else {
            link = this.campaignLink;
        }
        // const link = (this.hostingEnabled) ? 'https://app.ShuttleNomix.com/showCampaign?' : this.campaignLink;
        return link;
    }
    public set hostingLink(value: string) {
        if (value) {
            if (!value.endsWith('?')) {
                value += '?';
            }
        }
        this.keyValuePairService.setValue('[HostingLink]', value);
    }

    // liveTrackingLink
    // http://localhost:8100/trackerLive?ai=0&sbb=true
    public get liveTrackingLink(): string {
        const domainInfo = this.utilsService.getDomainInfo();
        const path = 'trackerLive?';
        const query = `ai=${this.accountProfile.uid}&sbb=false`;     // ai=account uid, sbb=show back button
        const link = domainInfo.properOrigin + '/' + path + query;

        return link;
    }
    public set liveTrackingLink(value: string) {
        this.keyValuePairService.setValue('[LiveTrackingLink]', value);
    }

    // numMilesToGo
    public get numMilesToGo(): number {
        // const text1 = (this.variables.numMilesToGo === '1') ? ' Mile' : ' Miles';
        // const text2 = this.variables.numMilesToGo + text1 + ' To Go';
        // return text2;
        return this.keyValuePairService.getValue('[NumMilesToGo]') as number;
    }
    public set numMilesToGo(value: number) {
        this.keyValuePairService.setValue('[NumMilesToGo]', value);
    }

    // numMinutes
    public get numMinutes(): number {
        // const allAboardSetting = this.settings?.allAboard;
        // const numMinutes = allAboardSetting.numMinutes;
        return this.keyValuePairService.getValue('[NumMinutes]') as number;
    }
    public set numMinutes(value: number) {
        this.keyValuePairService.setValue('[NumMinutes]', value);
    }

    // numMinutesFromNow
    public get numMinutesFromNow(): string {
        const now = this.dateService.now();
        const numMinutes = Number(this.numMinutes);
        const numMinutesFromNow = this.dateService.add(now, numMinutes, 'minutes', false);
        const str = this.dateService.formatTimestamp(numMinutesFromNow, Constants.DateTimeFormats.ShortTime2);
        return str;
    }

    // numMiles
    public get numMiles(): number {
        return this.keyValuePairService.getValue('[NumMiles]') as number;
    }
    public set numMiles(value: number) {
        this.keyValuePairService.setValue('[NumMiles]', value);
    }

    // optoutLink
    public get optoutLink(): string {
        const link = 'https://app.ShuttleNomix.com/campaignOptout?' + this.queryParams;
        return link;
    }

    public get queryParams(): string {
        return `an=${this.accountNumber}&cp=${this.cellPhone}&cpni=${this.incentiveSetId}`;
    }

    public get shuttleDateFormatted(): string {
        const customer = this.keyValuePairService.getValue('[Customer]') as TCustomer;
        if (!customer) {
            return '';
        }
        if (!customer.shuttleDates || customer.shuttleDates.length === 0) {
            return '';
        }
        const shuttleDates = customer?.shuttleDates as TTimestamp[];
        const sortedShuttleDates = this.sortShuttleDates(shuttleDates);
        // const sortedShuttleDates = shuttleDates;

        const mostRecentShuttleDate = (sortedShuttleDates) ? sortedShuttleDates[0] : this.dateService.now();
        const str = mostRecentShuttleDate ? this.dateService.formatTimestamp(mostRecentShuttleDate, DateTimeFormats.BigDate) : '';
        return str;
    }

    private sortShuttleDates(shuttleDates: TTimestamp[]): TTimestamp[] {
        const sortedShuttleDates = shuttleDates.sort((x: TTimestamp, y: TTimestamp) => {
            const a = this.dateService.toDate(x);   // x.toDate();
            const b = this.dateService.toDate(y);   // y.toDate();
            return (a.valueOf() - b.valueOf());
        });
        return sortedShuttleDates;
    }
    // toCoordinate
    public get toCoordinate(): TCoordinate {
        return this.keyValuePairService.getValue('[ToCoordinate]') as TCoordinate;
    }
    public set toCoordinate(value: TCoordinate) {
        this.keyValuePairService.setValue('[ToCoordinate]', value);
    }

    // TODO: Test this from sms.service(25);
    public async reset(accountNumber = ''): Promise<void> {
        this.setAllValuesToNull();
        const { accountProfile, dealership, settings } = await this.getObjects(accountNumber);
        this.accountProfile = accountProfile;
        this.dealership = dealership;
        this.settings = settings;

        // await this.loadDefaultValues();
    }

    private setAllValuesToNull() {
        // Not providing a value in setValue defaults to ''
        this.keyValuePairService.setValue('[AccountNumber]');
        this.keyValuePairService.setValue('[CampaignLink]');
        this.keyValuePairService.setValue('[CountryCode]');
        this.keyValuePairService.setValue('[CurrentCoordinate]');
        this.keyValuePairService.setValue('[CurrentDate]');
        this.keyValuePairService.setValue('[CurrentTime]');
        this.keyValuePairService.setValue('[Customer]');
        this.keyValuePairService.setValue('[CustomerName]');
        this.keyValuePairService.setValue('[DealershipName]');
        this.keyValuePairService.setValue('[DebugData]');
        this.keyValuePairService.setValue('[DepartureTime]');
        this.keyValuePairService.setValue('[Domain]');
        this.keyValuePairService.setValue('[FakeCustomer]');
        this.keyValuePairService.setValue('[FakeDepartureTime]');
        this.keyValuePairService.setValue('[HostingLink]');
        this.keyValuePairService.setValue('[LiveTrackingLink]');
        this.keyValuePairService.setValue('[NumMilesToGo]');
        this.keyValuePairService.setValue('[NumMinutes]');
        this.keyValuePairService.setValue('[NumMiles]');
        this.keyValuePairService.setValue('[OptOutLink]');
        this.keyValuePairService.setValue('[ShuttleDate]');
        this.keyValuePairService.setValue('[ShuttleDateFormatted]');
        this.keyValuePairService.setValue('[ToCoordinate]');
    }

    // private async loadDefaultValues() {
    //     try {
    //         const { accountProfile, settings } = await this.getObjects();
    //         this.accountProfile = accountProfile;
    //         this.settings = settings;

    //         // const settings: TSetting = await this.settingsService.get();
    //         // this.hostingEnabled = settings?.hosting.enabled;

    //         // const campaignLink = settings?.hosting.campaignHostingLink;
    //         // this.hostingLink = (this.hostingEnabled) ? 'https://app.ShuttleNomix.com/showCampaign?' : this.campaignLink;

    //         // const profile        = this.sessionService.getAccountProfile();
    //         // this.accountNumber   = accountProfile.accountNumber;
    //         // this.countryCode     = accountProfile.countryCode;
    //         // this.dealershipName  = accountProfile.dealershipName;

    //         // this.domain          = this.utilsService.getDomainInfo().domain;
    //         // this.fakeCustomer    = this.sessionService.getFakeCustomer();

    //         // this.dealershipName  = this.sessionService.getDealershipName();
    //         // this.domain          = this.sessionService.getDomain();
    //     } catch (err) {
    //         throw err;
    //     }
    // }

    // TODO: Test this method.
    private async getObjects(accountNumber: string): Promise<TRootSessionData> {
        const isLoggedIn: boolean = this.sessionService?.getIsLoggedIn();
        let accountProfile: TAccountProfile;
        let dealership: TDealership;
        let settings: TSetting;

        if (isLoggedIn) {
            accountProfile = this.sessionService.getAccountProfile();
            dealership = this.sessionService.getDealership();
            settings = await this.settingsService.get();
        } else {
            // We are not logged in. This was called via showCampaign. So we need to get our objects
            // via the passed in query parameter accountNumber.
            accountProfile = await this.accountProfileService.getAccountProfileByAccountNumber(accountNumber);
            dealership = await this.dealershipService.getAccountNumber(accountNumber);
            settings = await this.settingsService.getByAccountNumber(accountNumber);
        }

        const rootSessionData: TRootSessionData = {
            'accountProfile': accountProfile,
            'dealership': dealership,
            'settings': settings
        };
        return rootSessionData;
    }

    // private createCustomerLinks() {
    //     try {
    //         // let queryParams = '';
    //         // if (this.hostingEnabled) {
    //         //     // queryParams = `an=${this.accountNumber}&cu=${this.customerUid}`;
    //         //     this.campaignLink  = this.hostingLink + queryParams;
    //         // } else {
    //         //     this.campaignLink  = this.hostingLink;
    //         // }

    //         // this.optoutLink = 'https://app.ShuttleNomix.com/campaignOptout?' + queryParams;
    //     } catch (err) {
    //         throw err;
    //     }
    // }

    public doVariableReplacements(msg: string): string {
        // this.createCustomerLinks();

        // Only replace the variable with a value if we have a good value.
        msg = this.utilsService.replaceAll(msg, '[DealershipName]', this.dealershipName);
        msg = this.utilsService.replaceAll(msg, '[CampaignLink]', this.campaignLink);
        msg = this.utilsService.replaceAll(msg, '[CustomerName]', this.customerName);
        msg = this.utilsService.replaceAll(msg, '[CurrentDate]', this.currentDate);
        msg = this.utilsService.replaceAll(msg, '[CurrentTime]', this.currentTime);
        msg = this.utilsService.replaceAll(msg, '[CountryCode]', this.countryCode);
        msg = this.utilsService.replaceAll(msg, '[Distance]', this.distanceFormatted);
        msg = this.utilsService.replaceAll(msg, '[DepartureTime]', this.departureTime.toString());
        msg = this.utilsService.replaceAll(msg, '[Domain]', this.domain);
        msg = this.utilsService.replaceAll(msg, '[Newline]', '\n');
        msg = this.utilsService.replaceAll(msg, '[NumMiles]', this.numMiles.toString());
        msg = this.utilsService.replaceAll(msg, '[NumMilesToGo]', this.numMilesToGo.toString());
        msg = this.utilsService.replaceAll(msg, '[NumMinutes]', this.numMinutes.toString());
        msg = this.utilsService.replaceAll(msg, '[NumMinutesFromNow]', this.numMinutesFromNow);
        msg = this.utilsService.replaceAll(msg, '[OptoutLink]', this.optoutLink);
        msg = this.utilsService.replaceAll(msg, '[ShuttleDate]', this.shuttleDateFormatted);
        msg = this.utilsService.replaceAll(msg, '[LiveTrackingLink]', this.liveTrackingLink);

        // If we still have missing variables supply fake values
        msg = this.utilsService.replaceAll(msg, '[CustomerName]', this.fakeCustomer.customerName);
        msg = this.utilsService.replaceAll(msg, '[NumMiles]', '3');
        // msg = this.utilsService.replaceAll(msg, '[DepartureTime]', this.fakeDepartureTime);

        if (this.debugData) {
            msg += '\n[DebugData]=' + JSON.stringify(this.debugData);
        }

        return msg;
    }
}
